import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { JobStatusEnum, JobTypeEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { JobEventsContext, JobSnapshot, isTerminalJobStatus } from '@hooks/useJobs/JobEventsContext.tsx';
import { formatJobItemError } from '@hooks/useBEValidation/formatBEValidationMessage';

const BANNER_JOB_TYPES: string[] = [JobTypeEnum.LADEN_FACTOR_ACCEPT];

const JobStatusBannerComponent: React.FC = () => {
  const { jobs } = useContext(JobEventsContext);
  const { getAsyncJobDetail } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [trackedIds, setTrackedIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const active = [...jobs.values()].filter(s => BANNER_JOB_TYPES.includes(s.jobType) && !isTerminalJobStatus(s.status)).map(s => s.jobId);
    const added = active.filter(id => !trackedIds.includes(id));
    if (added.length > 0) setTrackedIds(prev => [...prev, ...added]);
  }, [jobs]);

  useEffect(() => {
    trackedIds
      .map(id => jobs.get(id))
      .filter(s => s != null && isTerminalJobStatus(s.status) && (s.failed > 0 || s.status === JobStatusEnum.ERROR) && errors[s.jobId] === undefined)
      .forEach(s => {
        setErrors(prev => ({ ...prev, [s.jobId]: '' }));
        getAsyncJobDetail(s.jobId, detail => {
          const item = detail.items.find(i => i.errorMessage != null || i.resultJson != null);
          let error;
          try {
            error = item?.resultJson ? JSON.parse(item.resultJson)?.error : undefined;
          } catch {
            error = undefined;
          }
          const message = formatJobItemError(error, item?.errorMessage ?? detail.job.errorMessage, tr) ?? '';
          setErrors(prev => ({ ...prev, [s.jobId]: message }));
        });
      });
  }, [jobs, trackedIds]);

  const visible = trackedIds.map(id => jobs.get(id)).filter((s): s is JobSnapshot => s != null);

  if (visible.length === 0) {
    return null;
  }

  const dismiss = () => {
    const keep = visible.filter(s => !isTerminalJobStatus(s.status)).map(s => s.jobId);
    setTrackedIds(keep);
  };

  const renderJob = (s: JobSnapshot) => {
    if (!isTerminalJobStatus(s.status)) {
      return (
        <div key={s.jobId} className="flex items-center gap-2 p-3 border-b border-gray-200 last:border-b-0">
          <span className="inline-block w-4 h-4 border-2 border-gray-200 rounded-full border-t-[var(--ringil3-brand-color)] animate-spin flex-shrink-0" />
          <span className="text-sm font-medium flex-1">{tr('jobStatus.ladenAccept.running', 'Accepting the carrier quote…')}</span>
        </div>
      );
    }
    if (s.failed > 0 || s.status === JobStatusEnum.ERROR) {
      return (
        <div key={s.jobId} className="flex items-center gap-2 p-3 border-b border-gray-200 last:border-b-0">
          <span className="text-sm font-medium text-[color:var(--ringil3-error-color,#ef4444)]">
            {tr('jobStatus.ladenAccept.failed', '✗ Accepting the carrier quote failed: {{msg}}', { msg: errors[s.jobId] ?? '' })}
          </span>
        </div>
      );
    }
    return (
      <div key={s.jobId} className="flex items-center gap-2 p-3 border-b border-gray-200 last:border-b-0">
        <span className="text-sm font-medium text-[color:var(--ringil3-success-color,#16a34a)]">
          {tr('jobStatus.ladenAccept.done', '✓ Carrier quote accepted')}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 max-w-md">
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <span className="font-semibold text-gray-800">{tr('jobStatus.title', 'Job progress')}</span>
        <button onClick={dismiss} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
          ✕
        </button>
      </div>
      <div className="max-h-96 overflow-y-auto">{visible.map(renderJob)}</div>
    </div>
  );
};

export default JobStatusBannerComponent;
